import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { Helmet } from "react-helmet-async";
import { toast } from "react-hot-toast";
import { useParams } from "react-router-dom";
import configuratorService from "../../services/mockupService";
import NcImage from "shared/NcImage/NcImage";
import {
  Settings,
  Eye,
  RotateCw,
  Layers,
  FileImage,
  Droplets,
  PaletteIcon,
  Sun,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Zap,
} from "lucide-react";
import { ArrowDownTrayIcon, SparklesIcon } from "@heroicons/react/24/solid";
import NcModal from "shared/NcModal/NcModal";
import {
  fetchUserLogos,
  selectFetchedUserLogos,
  selectUserLogoLoaded,
  selectUserLogoLoading,
} from "store/slices/logosSlice";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import LogoUploadeModel from "shared/LogoUploadModal/LogoUploadModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import GoBackBtn from "shared/GoBackBtn/GoBackBtn";
import LucideIcon from "utils/LucideIcon";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import {
  IConfiguratorData,
  IEditorState,
  ILogoData,
  IProductImage,
  ISelectOption,
} from "Interfaces/interface.mockupGenerator";
import {
  blendModeOptions,
  mockupGenerationMessages,
  outputFormatOptions,
  PlatformServices,
  PrintingTypeEnum,
  printingTypeOptions,
} from "data/tools.enum";
import mockupService from "../../services/mockupService";
import SliderInput from "../../components/Mockup/SliderInput";
import SelectInput from "../../components/Mockup/SelectInput";
import SelectionOverlay from "../../components/Mockup/SelectionOverlay";
import GenerationModalContent from "../../shared/ToolsGenerationModal/GenerationModalContent";
import { fetchUserMockups, selectUserMockups } from "store/slices/mockupSlice";
import ConfirmDialog from "shared/Dialog/ConfirmDialog ";
import { restrictGenerationLimit } from "utils/toolsUtilities";
import { selectUser } from "store/slices/authSlice";

const MockupGeneratorPage: React.FC = () => {
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availablePrintingTypes, setAvailablePrintingTypes] =
    useState<ISelectOption[]>(printingTypeOptions);
  const [isLoading, setIsLoading] = useState(false);
  const [isMockupGenerating, setIsMockupGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [mockupBlob, setMockupBlob] = useState<Blob | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const { productId } = useParams<{ productId: string }>();
  const dispatch = useAppDispatch();
  const [configs, setConfigs] = useState<IConfiguratorData[]>([]);
  const [productImages, setProductImages] = useState<IProductImage[]>([]);
  const userLogos = useAppSelector(selectFetchedUserLogos);
  const [logos, setLogos] = useState<ILogoData[]>([
    { url: "", image: null, error: null },
  ]);
  const [selectedLogoIndex, setSelectedLogoIndex] = useState<number>(0);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<IEditorState>({
    configId: null,
    coordinates: [0, 0],
    dimensions: [100, 100],
    cropToFit: false,
    rotation: 0,
    opacity: 1,
    blendMode: "normal",
    maintainAspectRatio: true,
    boundaryCoordinates: [0, 0, 100, 100],
    addShadow: false,
    shadowOffset: [0, 0],
    shadowBlur: 0,
    outputFormat: "png",
    printingType: PrintingTypeEnum.EMBROIDERY,
    printingIntensity: 1,
    selectedLogoIndex: 0,
    productName: "",
  });
  const [activePanel, setActivePanel] = useState<
    "logo" | "effects" | "export" | null
  >("logo");
  const imageRef = useRef<HTMLImageElement>(null);
  const isLogosLoading = useAppSelector(selectUserLogoLoading);
  const [productsLoading, setProductLoading] = useState(false);
  const isLogosLoaded = useAppSelector(selectUserLogoLoaded);
  const [logoUploadType, setLogoUploadType] = useState("upload");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoName, setLogoName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState<any>();
  const [logoError, setLogoError] = useState("");
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const generatedMockups = useAppSelector(selectUserMockups);
  const user = useAppSelector(selectUser);
  const is360Customizer = user?.emailAddress?.endsWith("@360customizer.com");

  const closeAddLogoDialogue = () => {
    setLogoModalVisible(false);
  };

  const handleAddLogo = () => {
    setLogoModalVisible(true);
    setLogoUploadType("upload");
    setLogoUrl("");
    setLogoName("");
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError("");
  };

  useEffect(() => {
    if (!isLogosLoaded) {
      dispatch(fetchUserLogos());
    }
    if (generatedImageUrl) {
      dispatch(fetchUserMockups());
    }
  }, [dispatch, isLogosLoaded, generatedImageUrl]);

  useEffect(() => {
    const loadProductData = async () => {
      if (!productId) {
        toast.error("No product ID provided.");
        return;
      }
      try {
        setProductLoading(true);
        const configurationData =
          await configuratorService.fetchMockupConfigurations(productId);
        setProductLoading(false);
        if (configurationData?.data) {
          setProductImages(configurationData.data.productImages || []);
          const printingTypes =
            configurationData.data.productConfigurations?.printing_type;
          if (
            printingTypes &&
            Array.isArray(printingTypes) &&
            printingTypes.length > 0
          ) {
            const filteredOptions = printingTypeOptions.filter((option) =>
              printingTypes.includes(option.value)
            );
            setAvailablePrintingTypes(filteredOptions);
            setEditorState((prev) => ({
              ...prev,
              printingType:
                filteredOptions[0]?.value || PrintingTypeEnum.EMBROIDERY,
              productName: configurationData.data.product?.name || "",
            }));
          } else {
            setAvailablePrintingTypes([
              { value: PrintingTypeEnum.EMBROIDERY, label: "Embroidery" },
            ]);
            setEditorState((prev) => ({
              ...prev,
              printingType: PrintingTypeEnum.EMBROIDERY,
              productName: configurationData.data.product?.name || "",
            }));
          }
          if (
            Array.isArray(configurationData.data.imageConfigurations) &&
            configurationData.data.imageConfigurations.length > 0
          ) {
            setConfigs(configurationData.data.imageConfigurations);
          } else {
            setConfigs([]);
            toast.error("No configurations available for this product.");
          }
        } else {
          setProductImages([]);
          setConfigs([]);
          setAvailablePrintingTypes([
            { value: PrintingTypeEnum.EMBROIDERY, label: "Embroidery" },
          ]);
          setEditorState((prev) => ({
            ...prev,
            printingType: PrintingTypeEnum.EMBROIDERY,
          }));
          toast.error("Failed to load product configurations.");
        }
      } catch (error) {
        setProductImages([]);
        setProductLoading(false);
        setConfigs([]);
        setAvailablePrintingTypes([
          { value: PrintingTypeEnum.EMBROIDERY, label: "Embroidery" },
        ]);
        setEditorState((prev) => ({
          ...prev,
          printingType: PrintingTypeEnum.EMBROIDERY,
        }));
        toast.error("Failed to load configurations.");
        console.error("Configuration fetch error:", error);
      }
    };
    loadProductData();
  }, [productId]);

  useEffect(() => {
    const fetchLogos = async () => {
      setLogos(userLogos);
      if (userLogos.length > 0 && userLogos[0].logo) {
        setSelectedLogoIndex(0);
        setEditorState((prev) => ({ ...prev, selectedLogoIndex: 0 }));
      } else {
        setSelectedLogoIndex(0);
        setEditorState((prev) => ({ ...prev, selectedLogoIndex: null }));
      }
    };
    fetchLogos();
  }, [userLogos]);

  useEffect(() => {
    if (productImages.length > 0 && configs.length > 0) {
      const currentImageId = productImages[selectedImageIndex]?.id;
      const firstConfig = configs.find(
        (c) => c.base_image_id === currentImageId
      );
      if (firstConfig) {
        setSelectedConfigId(firstConfig.id);
        setEditorState((prev) => ({
          ...prev,
          configId: firstConfig.id,
          coordinates: firstConfig.coordinates || [0, 0],
          dimensions: firstConfig.dimensions || [100, 100],
          boundaryCoordinates: firstConfig.dimensions
            ? [0, 0, firstConfig.dimensions[0], firstConfig.dimensions[1]]
            : [0, 0, 100, 100],
        }));
      } else {
        setSelectedConfigId(null);
        setEditorState((prev) => ({
          ...prev,
          configId: null,
          coordinates: [0, 0],
          dimensions: [100, 100],
          boundaryCoordinates: [0, 0, 100, 100],
        }));
        toast.error("No configurations found for the selected image.");
      }
    }
  }, [productImages, configs, selectedImageIndex]);

  const handleLogoSelect = (index: number) => {
    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => () => {
        setSelectedLogoIndex(index);
        setEditorState((prev) => ({ ...prev, selectedLogoIndex: index }));
        setGeneratedImageUrl(null);
        setMockupBlob(null);
      });
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setSelectedLogoIndex(index);
      setEditorState((prev) => ({ ...prev, selectedLogoIndex: index }));
    }
  };

  const handleShadowOffsetChange = (index: number, value: number) => {
    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => () => {
        setEditorState((prev) => {
          const newOffset: [number, number] = [
            ...(prev.shadowOffset || [0, 0]),
          ];
          newOffset[index] = value;
          return {
            ...prev,
            shadowOffset: newOffset,
            generatedImageUrl: null,
            mockupBlob: null,
          };
        });
      });
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setEditorState((prev) => {
        const newOffset: [number, number] = [...(prev.shadowOffset || [0, 0])];
        newOffset[index] = value;
        return { ...prev, shadowOffset: newOffset };
      });
    }
  };

  const handleConfigChange = (configId: string) => {
    const selectedConfig = configs.find((c) => c.id === configId);
    if (!selectedConfig) {
      console.error(`Configuration with ID ${configId} not found.`);
      toast.error("Selected configuration not found.");
      return;
    }
    const newState: IEditorState = {
      ...editorState,
      configId: selectedConfig.id,
      coordinates: selectedConfig.coordinates || [0, 0],
      dimensions: selectedConfig.dimensions || [100, 100],
      boundaryCoordinates: selectedConfig.dimensions
        ? [0, 0, selectedConfig.dimensions[0], selectedConfig.dimensions[1]]
        : [0, 0, 100, 100],
      rotation: 0,
      opacity: 1,
      blendMode: "normal" as "normal",
      addShadow: false,
      shadowOffset: [0, 0] as [number, number],
      shadowBlur: 0,
      cropToFit: false,
      outputFormat: "png" as "png",
      maintainAspectRatio: true,
      printingIntensity: 1,
      printingType: editorState.printingType || PrintingTypeEnum.EMBROIDERY,
      selectedLogoIndex: editorState.selectedLogoIndex,
      productName: editorState.productName,
    };

    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => () => {
        setSelectedConfigId(configId);
        setEditorState(newState);
        setGeneratedImageUrl(null);
        setMockupBlob(null);
      });
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setSelectedConfigId(configId);
      setEditorState(newState);
    }
  };

  const handleImageLoad = (img: HTMLImageElement) => {
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    const container =
      imageRef.current?.closest(".flex-1") || document.querySelector(".flex-1");

    const maxHeight = container
      ? Math.min(
        container.clientHeight || window.innerHeight * 0.7,
        window.innerHeight * 0.7
      )
      : window.innerHeight * 0.7;

    const maxWidth = container?.clientWidth || window.innerWidth * 0.9;

    let displayedHeight = maxHeight;
    let displayedWidth = (naturalWidth / naturalHeight) * maxHeight;

    if (displayedWidth > maxWidth) {
      displayedWidth = maxWidth;
      displayedHeight = (naturalHeight / naturalWidth) * maxWidth;
    }

    setImageDimensions({ width: displayedWidth, height: displayedHeight });
    setNaturalDimensions({ width: naturalWidth, height: naturalHeight });
  };

  const handleImageSelect = (index: number) => {
    const selectedImage = productImages[index];
    const firstConfig = configs.find(
      (c) => c.base_image_id === selectedImage.id
    );

    const action = () => {
      setSelectedImageIndex(index);
      if (!currentImage?.image_link) {
        setImageDimensions({ width: 0, height: 0 });
        setNaturalDimensions({ width: 0, height: 0 });
      }
      if (firstConfig) {
        handleConfigChange(firstConfig.id);
      } else {
        setSelectedConfigId(null);
        setEditorState((prev) => ({
          ...prev,
          configId: null,
          coordinates: [0, 0],
          dimensions: [100, 100],
          boundaryCoordinates: [0, 0, 100, 100],
          rotation: 0,
          opacity: 1,
          blendMode: "normal" as "normal",
          addShadow: false,
          shadowOffset: [0, 0] as [number, number],
          shadowBlur: 0,
          cropToFit: false,
          maintainAspectRatio: true,
          printingIntensity: 1,
          printingType: prev.printingType || PrintingTypeEnum.EMBROIDERY,
          selectedLogoIndex: prev.selectedLogoIndex,
          productName: prev.productName,
        }));
      }
      setGeneratedImageUrl(null);
      setMockupBlob(null);
    };

    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => action);
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
      if (firstConfig) setSelectedConfigId(firstConfig.id);
    } else {
      action();
    }
  };

  const handleGenerateMockup = async () => {
    if (
      !restrictGenerationLimit(
        PlatformServices.MOCKUP,
        generatedMockups.length,
        is360Customizer
      )
    ) {
      return;
    }

    if (userLogos.length === 0) {
      toast.error("Please upload at least one logo.");
      setActivePanel("logo");
      handleAddLogo();
      return;
    }

    if (selectedLogoIndex === null || !selectedConfigId) {
      toast.error(
        "Please ensure a product, a valid logo, and a configuration are selected."
      );
      return;
    }

    let messageInterval: ReturnType<typeof setInterval> | null = null;

    try {
      setIsModalOpen(true);
      setIsMockupGenerating(true);
      setError(null);
      setMessageIndex(0);

      const selectedConfig = configs.find((c) => c.id === selectedConfigId);
      const baseImage = productImages.find(
        (img) => img.id === selectedConfig?.base_image_id
      );
      const designImageUrl = userLogos[selectedLogoIndex].logo;

      const payload = {
        base_image_url: baseImage?.image_link || "",
        design_image_url: designImageUrl,
        coordinates: editorState.coordinates || [0, 0],
        dimensions: editorState.dimensions || [100, 100],
        crop_to_fit: editorState.cropToFit || false,
        rotation: editorState.rotation || 0,
        opacity: editorState.opacity || 1.0,
        blend_mode: editorState.blendMode || "normal",
        maintain_aspect_ratio: editorState.maintainAspectRatio || true,
        boundary_coordinates: editorState.boundaryCoordinates || [
          0, 0, 100, 100,
        ],
        add_shadow: editorState.addShadow || false,
        shadow_offset: editorState.shadowOffset || [0, 0],
        shadow_blur: editorState.shadowBlur || 0,
        output_format: editorState.outputFormat || "png",
        printing_type:
          editorState.printingType || PrintingTypeEnum.SCREEN_PRINTING,
        printing_intensity: editorState.printingIntensity || 1.0,
        product_name: editorState.productName || "",
      };

      messageInterval = setInterval(() => {
        setMessageIndex((prev) =>
          prev + 1 < mockupGenerationMessages.length ? prev + 1 : 0
        );
      }, 2000);

      const blob = await mockupService.generateMockup(payload);
      setMockupBlob(blob);

      const url = window.URL.createObjectURL(blob);
      setGeneratedImageUrl(url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Unknown error occurred during generation"
      );
    } finally {
      if (messageInterval) clearInterval(messageInterval);
      setIsMockupGenerating(false);
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleDownloadMockup = () => {
    if (!mockupBlob || !editorState.outputFormat) {
      toast.error("No mockup available to download.");
      return;
    }
    const url = window.URL.createObjectURL(mockupBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mockup_${new Date().toISOString()}.${editorState.outputFormat
      }`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetAfterSave = () => {
    setGeneratedImageUrl(null);
    setMockupBlob(null);
    setIsModalOpen(false);
  };

  const resetEffects = () => {
    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => () => {
        setEditorState((prev) => ({
          ...prev,
          rotation: 0,
          opacity: 1,
          blendMode: "normal",
          addShadow: false,
          shadowOffset: [0, 0],
          shadowBlur: 0,
          cropToFit: false,
          maintainAspectRatio: true,
          printingType: PrintingTypeEnum.SCREEN_PRINTING,
          printingIntensity: 1,
        }));
        setGeneratedImageUrl(null);
        setMockupBlob(null);
        toast.success("Effects reset to default.");
      });
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setEditorState((prev) => ({
        ...prev,
        rotation: 0,
        opacity: 1,
        blendMode: "normal",
        addShadow: false,
        shadowOffset: [0, 0],
        shadowBlur: 0,
        cropToFit: false,
        maintainAspectRatio: true,
        printingType: PrintingTypeEnum.SCREEN_PRINTING,
        printingIntensity: 1,
      }));
      toast.success("Effects reset to default.");
    }
  };

  const handleConfirmReset = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setGeneratedImageUrl(null);
    setMockupBlob(null);
    setHasPendingChanges(false);
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setShowConfirmDialog(false);
    setHasPendingChanges(false);
  };

  const handleEditorStateChange = (key: keyof IEditorState, value: any) => {
    if (generatedImageUrl && !hasPendingChanges) {
      setPendingAction(() => () => {
        setEditorState((prev) => {
          const newState = { ...prev, [key]: value };
          if (key === "addShadow" && !value) {
            newState.shadowOffset = [0, 0];
            newState.shadowBlur = 0;
          }
          return newState;
        });
        setGeneratedImageUrl(null);
        setMockupBlob(null);
      });
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setEditorState((prev) => {
        const newState = { ...prev, [key]: value };
        if (key === "addShadow" && !value) {
          newState.shadowOffset = [0, 0];
          newState.shadowBlur = 0;
        }
        return newState;
      });
    }
  };

  const handleTabChange = (id: "logo" | "effects" | "export") => {
    if (userLogos.length === 0 && id !== "logo") {
      toast.error("Please upload at least one logo first.");
      setActivePanel("logo");
      return;
    }
    if (generatedImageUrl && activePanel !== id && !hasPendingChanges) {
      setPendingAction(() => () => setActivePanel(id));
      setShowConfirmDialog(true);
      setHasPendingChanges(true);
    } else {
      setActivePanel(id);
    }
  };

  const currentImage = productImages[selectedImageIndex];
  const currentImageConfigs = configs.filter(
    (c) => c.base_image_id === currentImage?.id
  );
  const selectedConfig = configs.find((c) => c.id === selectedConfigId);

  if (!productId) {
    return <ErrorFallback message="No product ID provided." />;
  }

  if (productImages.length === 0 && !productsLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          This product is not configured for mockup generation.
        </h1>
        <GoBackBtn variant="basic" />
      </div>
    );
  }

  if (currentImageConfigs.length === 0 && !productsLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <SparklesIcon className="w-12 h-12 text-indigo-500 mb-4 opacity-60" />
        <h2 className="text-xl font-bold text-gray-800 text-center">
          No configurations available for this image.
        </h2>
        <p className="text-sm text-gray-600 mt-2 text-center">
          Please select a different image or contact support.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 to-indigo-50 min-h-screen">
      <Helmet>
        <title>360 Mockup Generator</title>
      </Helmet>

      <div className="bg-white/80">
        <div className="mx-auto">
          <GoBackBtn className="mb-4 mx-4" />
        </div>
      </div>
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-start gap-3 md:gap-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Mockup Studio
                </h1>
                <p className="text-sm text-gray-600">
                  Professional mockup creation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {productsLoading ? (
        <div className="flex justify-center items-center h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
          <img src={infiniteLoader} alt="Loading..." className="w-[14%]" />
        </div>
      ) : (
        <main className="p-[2rem] h-auto lg:h-screen flex-1 flex flex-col lg:flex-row gap-4 lg:max-w-[100vw] overflow-hidden">
          <aside
            className={`${activePanel ? "flex" : "hidden"
              } lg:flex lg:w-[30%] bg-white/90 backdrop-blur-md rounded-2xl shadow-lg border border-white/30 flex-col transition-all duration-300`}
            style={{ flexBasis: "30%", minWidth: "30%" }}
          >
            <div className="flex border-b border-gray-100 bg-white/70">
              {[
                { id: "logo", label: "Logos", icon: FileImage },
                { id: "effects", label: "Effects", icon: SparklesIcon },
                { id: "export", label: "Format", icon: ArrowDownTrayIcon },
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id as any)}
                  className={`flex-1 p-3 text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${activePanel === id
                    ? "bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 border-b-2 border-indigo-500"
                    : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 h-full">
              {activePanel === "logo" && (
                <div className="h-full flex flex-col justify-between">
                  <div className="space-y-4 max-h-[70%] overflow-y-auto">
                    <h3 className="text-base font-semibold text-gray-800 flex items-center">
                      <FileImage className="w-5 h-5 mr-2 text-indigo-500" />
                      Your Logos
                    </h3>
                    {isLogosLoading && !isLogosLoaded ? (
                      <div className="flex justify-center items-center">
                        <img
                          src={infiniteLoader}
                          alt="Loading..."
                          className="w-[14%]"
                        />
                      </div>
                    ) : userLogos.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-4 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-600 mb-2 text-center">
                          No logos available. Please upload at least one logo to
                          continue.
                        </p>
                        <ButtonPrimary onClick={handleAddLogo}>
                          Upload Logo
                        </ButtonPrimary>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-8 lg:grid-cols-4 gap-2 md:gap-4 lg:gap-2">
                        <div className="flex justify-center items-center w-fit h-16">
                          <button
                            onClick={handleAddLogo}
                            className="border-2 w-fit border-dashed border-gray-300 rounded-xl text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 flex items-center"
                          >
                            {logoPreview ? (
                              <div>
                                <img
                                  src={logoPreview}
                                  alt="Preview"
                                  className="max-h-32 mx-auto rounded-lg checkerboard"
                                />
                              </div>
                            ) : (
                              <LucideIcon
                                name="Plus"
                                className="h-16 w-16 text-gray-400 mx-auto"
                              />
                            )}
                          </button>
                        </div>
                        {logos.length > 0 &&
                          userLogos.map((logo: any, index: any) => (
                            <div
                              key={index}
                              className={`relative rounded-lg w-fit border-2 cursor-pointer transition-all duration-200 overflow-hidden ${selectedLogoIndex === index
                                ? "border-indigo-500 ring-2 ring-indigo-200 shadow-md"
                                : "border-gray-200 hover:border-indigo-400 hover:shadow-sm"
                                }`}
                              onClick={() => handleLogoSelect(index)}
                            >
                              {logo.logo ? (
                                <NcImage
                                  containerClassName="w-16 h-16 flex items-center justify-center checkerboard"
                                  src={logo.logo}
                                  alt={`Logo ${index + 1}`}
                                  className="h-auto w-fit"
                                />
                              ) : logo.error ? (
                                <div className="w-full h-14 bg-red-50 flex items-center justify-center text-xs text-red-500">
                                  Error
                                </div>
                              ) : (
                                <div className="w-full h-14 bg-gray-100 animate-pulse"></div>
                              )}
                              {selectedLogoIndex === index && (
                                <div className="absolute top-1 right-1 w-2.5 h-2.5 bg-indigo-500 rounded-full"></div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 mt-4">
                    <ButtonSecondary
                      onClick={() => handleTabChange("effects")}
                      variant="info"
                      className="flex-1 flex items-center justify-center gap-2"
                      disabled={userLogos.length === 0}
                    >
                      <ArrowRight className="w-4 h-4" />
                      Apply Effects
                    </ButtonSecondary>
                  </div>
                </div>
              )}

              {activePanel === "effects" && (
                <div className="h-full flex flex-col justify-between">
                  <div className="space-y-4 max-h-[70%] overflow-y-auto">
                    <h3 className="text-base font-semibold text-gray-800 flex items-center">
                      <Settings className="w-5 h-5 mr-2 text-indigo-500" />
                      Effects
                    </h3>
                    <div className="space-y-4">
                      <SliderInput
                        label="Rotation"
                        value={editorState.rotation}
                        onChange={(value) =>
                          handleEditorStateChange("rotation", value)
                        }
                        min={0}
                        max={360}
                        unit="°"
                        icon={RotateCw}
                      />
                      <SliderInput
                        label="Opacity"
                        value={editorState.opacity}
                        onChange={(value) =>
                          handleEditorStateChange("opacity", value)
                        }
                        min={0}
                        max={1}
                        step={0.01}
                        icon={Eye}
                      />
                      <SelectInput
                        label="Blend Mode"
                        value={editorState.blendMode}
                        onChange={(value) =>
                          handleEditorStateChange("blendMode", value)
                        }
                        options={blendModeOptions}
                        icon={Layers}
                      />
                    </div>
                    {/* <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2">
                        Shadow
                      </h4>
                      <ToggleInput
                        label="Add Shadow"
                        value={editorState.addShadow}
                        onChange={(value) => handleEditorStateChange("addShadow", value)}
                        icon={Droplets}
                      />
                      {editorState.addShadow && (
                        <div className="pl-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-600">
                                X Offset
                              </label>
                              <select
                                value={editorState.shadowOffset?.[0] ?? 0}
                                onChange={(e) => handleShadowOffsetChange(0, parseInt(e.target.value, 10))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400"
                              >
                                {Array.from({ length: 11 }, (_, i) => i - 5).map((val) => (
                                  <option key={val} value={val}>
                                    {val}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-600">
                                Y Offset
                              </label>
                              <select
                                value={editorState.shadowOffset?.[1] ?? 0}
                                onChange={(e) => handleShadowOffsetChange(1, parseInt(e.target.value, 10))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400"
                              >
                                {Array.from({ length: 11 }, (_, i) => i - 5).map((val) => (
                                  <option key={val} value={val}>
                                    {val}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <SliderInput
                            label="Shadow Blur"
                            value={editorState.shadowBlur}
                            onChange={(value) => handleEditorStateChange("shadowBlur", value)}
                            min={0}
                            max={20}
                            unit="px"
                            icon={Zap}
                          />
                        </div>
                      )}
                    </div> */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2">
                        Printing
                      </h4>
                      <SelectInput
                        label="Type"
                        value={editorState.printingType}
                        onChange={(value) =>
                          handleEditorStateChange("printingType", value)
                        }
                        options={availablePrintingTypes}
                        icon={PaletteIcon}
                      />
                      <SliderInput
                        label="Intensity"
                        value={editorState.printingIntensity}
                        onChange={(value) =>
                          handleEditorStateChange("printingIntensity", value)
                        }
                        min={0}
                        max={1}
                        step={0.1}
                        icon={Sun}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 mt-3">
                    <ButtonSecondary
                      onClick={() => handleTabChange("logo")}
                      className="flex-1 flex items-center justify-center gap-2"
                      variant="info"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Choose Logo
                    </ButtonSecondary>
                    <ButtonSecondary
                      onClick={resetEffects}
                      variant="info"
                      className="flex-1 flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Reset Effects
                    </ButtonSecondary>
                    <ButtonSecondary
                      onClick={() => handleTabChange("export")}
                      className="flex-1 flex items-center justify-center gap-2"
                      variant="info"
                    >
                      <ArrowRight className="w-4 h-4" />
                      Choose Format & Generate
                    </ButtonSecondary>
                    <ButtonPrimary
                      loading={isMockupGenerating}
                      disabled={isMockupGenerating}
                      onClick={handleGenerateMockup}
                    >
                      <SparklesIcon className="w-4 h-4" />
                      Generate Mockup
                    </ButtonPrimary>
                  </div>
                </div>
              )}

              {activePanel === "export" && (
                <div className="flex flex-col gap-4 justify-between">
                  <div className="space-y-4">
                    <h3 className="text-base font-semibold text-gray-800 flex items-center">
                      <FileImage className="w-5 h-5 mr-2 text-indigo-500" />
                      Choose File Format
                    </h3>
                    <SelectInput
                      value={editorState.outputFormat}
                      onChange={(value) =>
                        handleEditorStateChange("outputFormat", value)
                      }
                      options={outputFormatOptions}
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <ButtonSecondary
                      onClick={() => handleTabChange("effects")}
                      variant="info"
                      className="flex-1 flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Add Effects
                    </ButtonSecondary>
                    <ButtonPrimary
                      loading={isMockupGenerating}
                      disabled={isMockupGenerating}
                      onClick={handleGenerateMockup}
                    >
                      <SparklesIcon className="w-4 h-4" />
                      Generate Mockup
                    </ButtonPrimary>
                  </div>
                </div>
              )}
            </div>
          </aside>

          <div
            className="flex-1 flex flex-col min-h-0 lg:w-[70%]"
            style={{ flexBasis: "70%", minWidth: "70%" }}
          >
            <div className="lg:hidden mb-3">
              <button
                onClick={() => {
                  if (generatedImageUrl && !hasPendingChanges) {
                    setPendingAction(
                      () => () => setActivePanel(activePanel ? null : "logo")
                    );
                    setShowConfirmDialog(true);
                    setHasPendingChanges(true);
                  } else {
                    setActivePanel(activePanel ? null : "logo");
                  }
                }}
                className="w-full py-2.5 bg-white/90 backdrop-blur-md rounded-lg shadow-sm border border-white/30 text-gray-700 font-medium flex items-center justify-center gap-2 transition-all"
              >
                <Settings className="w-4 h-4" />
                {activePanel ? "Hide Controls" : "Show Controls"}
              </button>
            </div>

            <div className="flex-1 bg-white/90 backdrop-blur-md rounded-2xl shadow-lg border border-white/30 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-white/70">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-indigo-500" />
                  Preview
                </h2>
              </div>
              <div className="p-4 flex flex-col md:flex-row gap-4 flex-1 min-h-0">
                <div className="flex flex-row md:flex-col gap-2 lg:w-20 overflow-x-auto overflow-y-auto">
                  {productImages.map((image, index) => (
                    <div
                      key={index}
                      className={`flex-shrink-0 w-16 h-16 lg:w-18 lg:h-18 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-200 ${selectedImageIndex === index
                        ? "border-indigo-500 ring-2 ring-indigo-200 shadow-md"
                        : "border-gray-200 hover:border-indigo-400 hover:shadow-sm"
                        }`}
                      onClick={() => handleImageSelect(index)}
                    >
                      <NcImage
                        containerClassName="w-full h-full"
                        src={image.image_link}
                        alt={`Thumbnail ${index}`}
                        className="object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-1 relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-inner overflow-auto">
                    <div
                      className="flex items-center justify-center w-full h-full"
                      style={{ minHeight: "200px" }}
                    >
                      <div
                        className="relative"
                        style={{
                          width: `${imageDimensions.width}px`,
                          height: `${imageDimensions.height}px`,
                          maxWidth: "100%",
                          maxHeight: "100%",
                        }}
                      >
                        <NcImage
                          containerClassName="w-full h-full"
                          src={generatedImageUrl || currentImage?.image_link}
                          alt="Product Preview"
                          className="object-contain w-full h-full transition-opacity duration-300"
                          ref={imageRef}
                          onImageLoad={handleImageLoad}
                        />
                        {!generatedImageUrl && selectedConfig && (
                          <SelectionOverlay
                            imageWidth={imageDimensions.width || 1}
                            imageHeight={imageDimensions.height || 1}
                            selectedConfig={selectedConfig}
                            naturalWidth={naturalDimensions.width || 1}
                            naturalHeight={naturalDimensions.height || 1}
                          />
                        )}
                      </div>
                    </div>
                    {generatedImageUrl && (
                      <div className="absolute bottom-4 right-2 transform -translate-x-1/2 z-20 flex gap-2">
                        <ButtonPrimary
                          onClick={handleDownloadMockup}
                          className="max-w-fit"
                          disabled={!mockupBlob || isLoading}
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          <span className="hidden sm:inline">Download</span>
                          <span className="sm:hidden">Download</span>
                        </ButtonPrimary>
                      </div>
                    )}
                  </div>
                  <div
                    className="mt-4 flex max-w-full gap-2 overflow-x-auto pb-2"
                    style={{ minWidth: "300px", maxWidth: "100%" }}
                  >
                    {currentImageConfigs.map((config, index) => (
                      <div
                        key={config.id}
                        onClick={() => handleConfigChange(config.id)}
                        className={`flex-shrink-0 p-3 rounded-lg cursor-pointer transition-all duration-200 border-2 min-w-[120px] ${selectedConfigId === config.id
                          ? "border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-md"
                          : "border-gray-200 bg-white hover:border-indigo-400 hover:shadow-sm"
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-sm text-gray-800">
                            Logo Area {index + 1}
                          </h4>
                          {selectedConfigId === config.id && (
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full"></div>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {config.dimensions.join(" × ")} px
                        </p>
                      </div>
                    ))}
                    {currentImageConfigs.length < 3 && (
                      <div className="flex-shrink-0 w-[120px]" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      )}

      <NcModal
        isOpenProp={isModalOpen}
        onCloseModal={() => setIsModalOpen(false)}
        modalTitle=""
        contentExtraClass="max-w-md"
        renderContent={() => (
          <GenerationModalContent
            isGenerating={isMockupGenerating}
            generationMessages={mockupGenerationMessages}
            error={error}
            onSave={() => Promise.resolve()}
            onClose={() => setIsModalOpen(false)}
            messageIndex={messageIndex}
            setMessageIndex={setMessageIndex}
            generationItemName="mockup"
          />
        )}
      />
      {logoModalVisible && <LogoUploadeModel onClose={closeAddLogoDialogue} />}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          handleCancel();
        }}
        onConfirm={handleConfirmReset}
        title="Are you sure?"
        message="This will reset your current mockup preview and settings."
      />
    </div>
  );
};

export default MockupGeneratorPage;
